import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Info, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void> | void
  submitting?: boolean
  // Top-level error (e.g. "Invalid email or password.") from the auth call.
  formError?: string | null
  onDismissFormError?: () => void
  // Optional ref-like API for demo auto-fill + submit.
  formId?: string
  defaultValues?: Partial<LoginFormData>
}

export function LoginForm({
  onSubmit,
  submitting,
  formError,
  onDismissFormError,
  formId = 'login-form',
  defaultValues,
}: LoginFormProps) {
  const { t } = useTranslation('auth')
  const [showPassword, setShowPassword] = useState(false)
  const hasTopLevelError = !!formError
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '', ...defaultValues },
  })

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className='flex flex-col gap-8'
    >
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-1'>
          <Label
            htmlFor='email'
            className='text-base font-medium text-[#171A20]'
          >
            {t('login.emailLabel')}
          </Label>
          <Input
            id='email'
            type='email'
            autoComplete='email'
            maxLength={100}
            placeholder={t('login.emailPlaceholder')}
            aria-invalid={!!errors.email}
            className={cn(
              'h-11 rounded-xl border border-transparent bg-[#f5f7fb] px-4 py-3 text-sm text-[#171A20] shadow-none placeholder:text-[#3D434E] focus-visible:border-[#FF5A1F]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/12 focus-visible:ring-offset-0',
              hasTopLevelError &&
                'border-[#FF6B57] focus-visible:border-[#FF6B57] focus-visible:ring-[#FF6B57]/12',
              errors.email &&
                'border-[#EC221F] focus-visible:border-[#EC221F] focus-visible:ring-[#EC221F]/12'
            )}
            {...register('email')}
          />
          {errors.email && (
            <p className='text-sm font-normal leading-[1.4] text-[#EC221F]'>
              {errors.email.message}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between gap-3'>
            <Label
              htmlFor='password'
              className='text-base font-medium text-[#171A20]'
            >
              {t('login.passwordLabel')}
            </Label>
            <Link
              to='/forgot-password'
              className='text-sm font-medium text-[#FF5A1F] transition-colors hover:text-[#FF5A1F]/80'
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
          <div className='relative'>
            <Input
              id='password'
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              maxLength={64}
              placeholder={t('login.passwordPlaceholder')}
              aria-invalid={!!errors.password}
              className={cn(
                'h-11 rounded-xl border border-transparent bg-[#f5f7fb] px-4 py-3 pr-11 text-sm text-[#171A20] shadow-none placeholder:text-[#3D434E] focus-visible:border-[#FF5A1F]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/12 focus-visible:ring-offset-0',
                hasTopLevelError &&
                  'border-[#FF6B57] focus-visible:border-[#FF6B57] focus-visible:ring-[#FF6B57]/12',
                errors.password &&
                  'border-[#EC221F] focus-visible:border-[#EC221F] focus-visible:ring-[#EC221F]/12'
              )}
              {...register('password')}
            />
            <button
              type='button'
              onClick={() => setShowPassword(s => !s)}
              className='absolute right-4 top-1/2 -translate-y-1/2 text-[#3D434E] transition-colors hover:text-[#171A20]'
              aria-label={
                showPassword ? t('password.hide') : t('password.show')
              }
            >
              {showPassword ? (
                <EyeOff className='h-4 w-4' />
              ) : (
                <Eye className='h-4 w-4' />
              )}
            </button>
          </div>
          {errors.password && (
            <p className='text-sm font-normal leading-[1.4] text-[#EC221F]'>
              {errors.password.message}
            </p>
          )}
        </div>
      </div>

      {formError && (
        <div
          role='alert'
          className='relative rounded-2xl border border-[#FF6B57] bg-[#FFF3F0] p-4'
        >
          <div className='flex gap-3'>
            <Info
              className='mt-0.5 h-5 w-5 shrink-0 text-[#A61B11]'
              aria-hidden
            />
            <div className='flex min-w-0 flex-1 flex-col gap-1 pr-8'>
              <p className='text-base font-semibold leading-[1.4] text-[#7A231C]'>
                {t('login.warningTitle')}
              </p>
              <p className='text-base font-normal leading-[1.4] text-[#7A231C]'>
                {formError}
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={onDismissFormError}
            className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-[#A61B11] hover:bg-[#EC221F]/10'
            aria-label={t('login.dismissError')}
          >
            <X className='h-4 w-4' />
          </button>
        </div>
      )}

      <div className='pt-1'>
        <Button
          type='submit'
          className='h-11 w-full rounded-xl border border-[#FF5A1F] bg-[#FF5A1F] px-4 text-base font-medium text-white shadow-[0_12px_24px_rgba(255,90,31,0.2)] transition-transform hover:translate-y-[-1px] hover:bg-[#FF5A1F]/92'
          disabled={submitting}
        >
          {submitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          {t('login.submit')}
        </Button>
      </div>
    </form>
  )
}

export default LoginForm
